// SPEC-415 T-703 ③ P1 — 前端 WebP 接力压缩（relay compression）
//
// 图像落地后（JOB_UPDATE done / AGENT_RESULT add_image·set_image），对 >400KB 的自家 KV 图
// 在浏览器里重压为 WebP q85（不支持则 JPEG q85），持久重传（无 ttlDays），再由 store 换 src。
// 旧 key 由 ② 孤儿 GC 兜底回收，闭环。预期 2MB → ~300KB（-85%），到顶周期 周级 → 季度级。
//
// 硬约束（spec）：
//  - 任一环节失败 = 静默放弃，原图保留（不劣于现状），绝不 throw 到调用方。
//  - 竞态 guard：替换前对象仍存在且 src 仍 === oldSrc 才换。guard 在 store 的 IMG_RELAYED
//    reducer 内做（原子），本模块只负责「压缩 + 重传」，返回新 URL 或 null。
//  - 无压缩收益（out.size >= 原 size）不上传：省 KV 写配额（1000/天），也避免负优化。
//  - 已是 WebP 的图不重复接力。
//
// ⚠️ worker 配套改动（同 PR）：index.ts /api/upload 的 MIME 正则只收 png|jpeg，
//    必须加 webp，否则本模块上传一律 400（wake9 实勘 L650）。
import { uploadImage } from '../api';

const RELAY_MIN_BYTES = 400 * 1024; // >400KB 才接力（brief 定值）
const RELAY_QUALITY = 0.85;         // q85（brief 定值）

/** 同一 srcUrl 不并发接力（re-render / 多对象共享 src / 重复 landing）。 */
const inFlight = new Set<string>();

/**
 * 压缩 + 持久重传 srcUrl 指向的 KV 图。
 * @returns 新 /api/img/<key> URL；null = 静默放弃（小图/已 WebP/无收益/任何失败），调用方什么都不做。
 */
export async function relayCompress(srcUrl: string, signal?: AbortSignal): Promise<string | null> {
  if (typeof srcUrl !== 'string' || !srcUrl.includes('/api/img/')) return null; // 只接力自家 KV 图
  if (inFlight.has(srcUrl)) return null;
  inFlight.add(srcUrl);
  try {
    const resp = await fetch(srcUrl, { signal });
    if (!resp.ok) return null;
    // content-length 头先筛（HEAD 无此头、GET 有——cf-kv-capacity-audit 实证）；缺头则下载后按 blob.size 筛
    const lenHdr = Number(resp.headers.get('content-length'));
    if (Number.isFinite(lenHdr) && lenHdr > 0 && lenHdr <= RELAY_MIN_BYTES) return null;

    const blob = await resp.blob();
    if (blob.size <= RELAY_MIN_BYTES) return null;
    if (blob.type === 'image/webp') return null; // 已是 WebP，不重复接力

    const bitmap = await createImageBitmap(blob);
    let out: Blob | null;
    try {
      out = await encodeCanvas(bitmap, 'image/webp');
      // Safari 等：WebP 编码不支持时 canvas 会回落 image/png（或返回 null）→ JPEG 兜底
      if (!out || out.type !== 'image/webp') out = await encodeCanvas(bitmap, 'image/jpeg');
    } finally {
      bitmap.close();
    }
    if (!out || out.size >= blob.size) return null; // 无收益不上传

    const dataUrl = await blobToDataUrl(out);
    const newUrl = await uploadImage(dataUrl, undefined, signal); // 无 ttlDays = 持久
    if (typeof newUrl !== 'string' || !newUrl.includes('/api/img/')) return null;
    return newUrl;
  } catch {
    return null; // 静默放弃：原图保留
  } finally {
    inFlight.delete(srcUrl);
  }
}

/** OffscreenCanvas 优先（SPEC-402 像素采样同路径），无则 HTMLCanvasElement.toBlob 兜底。原尺寸，不缩放。 */
async function encodeCanvas(bitmap: ImageBitmap, type: 'image/webp' | 'image/jpeg'): Promise<Blob | null> {
  try {
    if (typeof OffscreenCanvas !== 'undefined') {
      const oc = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = oc.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      return await oc.convertToBlob({ type, quality: RELAY_QUALITY });
    }
    const c = document.createElement('canvas');
    c.width = bitmap.width;
    c.height = bitmap.height;
    const ctx2 = c.getContext('2d');
    if (!ctx2) return null;
    ctx2.drawImage(bitmap, 0, 0);
    return await new Promise<Blob | null>((res) => c.toBlob((b) => res(b), type, RELAY_QUALITY));
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(fr.error ?? new Error('FileReader failed'));
    fr.readAsDataURL(blob);
  });
}
